const ADCO_MIN_VECTOR_LENGTH = 10;
const ADCO_ASSET_TYPE_PATTERN = /(?:^RMBS$|NON[-\s]?AGENCY|AGENCY RMBS|REVERSE MORTGAGE)/;
const ADCO_VECTOR_FIELD_PATTERN = /^(?:CPR|CDR|SEV(?:ERITY)?|DLQ|DLNQ(?:60|90|180)?|DELINQUENCY)$/;

const normalizeFieldKey = fieldKey => String(fieldKey).trim().toUpperCase().replace(/[\s_-]+/g, '');

const getAssetType = rowData => String(
  rowData?.Type || rowData?.['Asset Type'] || rowData?.TYPE || rowData?.ASSET_TYPE || ''
).trim().toUpperCase();

export const isAdcoEligibleSecurity = rowData => {
  const assetType = getAssetType(rowData);
  return ADCO_ASSET_TYPE_PATTERN.test(assetType) && !assetType.includes('SFR');
};

// The two ways an Intex-tab ADCO run models RMBS (the run's adco_cshf flag): ADCO's own
// loan-level rates averaged per security, or rates derived from ADCO's summed loan cashflows.
export const adcoMethodLabel = adcoCshf => (adcoCshf ? 'ADCO Cashflow' : 'ADCO');

export const isAdcoVectorField = fieldKey => ADCO_VECTOR_FIELD_PATTERN.test(
  normalizeFieldKey(fieldKey)
);

export const isAdcoGeneratedVector = (fieldKey, value, rowData) => {
  if (!isAdcoVectorField(fieldKey) || typeof value !== 'string') return false;

  if (!isAdcoEligibleSecurity(rowData)) return false;

  const values = value.trim().split(/\s+/);
  return values.length >= ADCO_MIN_VECTOR_LENGTH
    && values.every(item => item !== '' && Number.isFinite(Number(item.replace(/%$/, ''))));
};

const ADCO_SOURCE_FIELD_BY_DEAL_FIELD = {
  CPR: 'CRR',
  CDR: 'CDR',
  SEV: 'SEV',
  SEVERITY: 'SEV',
  DLQ: 'DLNQ60',
  DLNQ: 'DLNQ60',
  DLNQ60: 'DLNQ60',
  DELINQUENCY: 'DLNQ60',
};

const ADCO_SOURCE_FIELDS = new Set(Object.values(ADCO_SOURCE_FIELD_BY_DEAL_FIELD));
const STATIC_PERCENTAGE_PATTERN = /^(?:\d+(?:\.\d*)?|\.\d+)%?$/;

const parseStaticPercentage = value => {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value >= 0 && value <= 100 ? value : null;
  }
  if (typeof value !== 'string') return null;

  const normalized = value.trim();
  if (!STATIC_PERCENTAGE_PATTERN.test(normalized)) return null;

  const numericValue = Number(normalized.replace(/%$/, ''));
  return Number.isFinite(numericValue) && numericValue >= 0 && numericValue <= 100
    ? numericValue
    : null;
};

export const getStaticAdcoOverrides = rowData => {
  if (!isAdcoEligibleSecurity(rowData)) return {};

  return Object.entries(rowData || {}).reduce((overrides, [fieldKey, value]) => {
    const sourceField = ADCO_SOURCE_FIELD_BY_DEAL_FIELD[normalizeFieldKey(fieldKey)];
    const numericValue = parseStaticPercentage(value);
    if (sourceField && numericValue !== null && !isAdcoGeneratedVector(fieldKey, value, rowData)) {
      overrides[sourceField] = numericValue;
    }
    return overrides;
  }, {});
};

export const applyStaticAdcoOverrides = (adcoData, overrides = {}) => {
  if (!Array.isArray(adcoData) || adcoData.length === 0) return null;

  const activeOverrides = Object.fromEntries(Object.entries(overrides).flatMap(([field, value]) => {
    const numericValue = parseStaticPercentage(value);
    return ADCO_SOURCE_FIELDS.has(field) && numericValue !== null
      ? [[field, numericValue]]
      : [];
  }));
  if (Object.keys(activeOverrides).length === 0) return adcoData;

  return adcoData.map(row => ({ ...row, ...activeOverrides }));
};

export const withoutAdcoGeneratedVectors = rowData => Object.fromEntries(
  Object.entries(rowData || {}).map(([fieldKey, value]) => [
    fieldKey,
    isAdcoGeneratedVector(fieldKey, value, rowData) ? '' : value,
  ])
);
